import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import ApplicationFormFooter from '../../pages/Application/ApplicationFormFooter';
import EvaluationFormHeader from '../../pages/EvaluationNew/components/EvaluationFormHeader';
import EvaluationInput from '../../pages/EvaluationNew/components/EvaluationInput';
import InputDatePicker from '../../pages/EvaluationNew/components/InputDatePicker';
import { request } from '../../utils/api';
import { displayDateUS } from '../../utils/dateUtils';
import CorrectionRequestAlert from './CorrectionRequestAlert';
import useCurrentUser from '../../hooks/useCurrentUser';
import { resolveFormTenantId } from '../../utils/tenantContext';
import {
    pickRecordFromListResult,
    parseRecordId,
} from '../../utils/applicationFormRecord';
import {
    buildWelcomeLetterPayload,
    isWelcomeLetterContractorForm,
    isWelcomeLetterGroup1,
    mapWelcomeLetterToForm,
} from '../../utils/welcomeLetterForm';

const BaseWelcomeLetter = ({
    role,
    items,
    totalPages = 2,
    title,
    apiEndpoint = '/welcome-letters',
    fetchEndpoint = '/welcome-letters',
    defaultValues = {},
    splitIndex = 10,
    onClose
}) => {
    const isGroup1 = isWelcomeLetterGroup1(role);
    const isGroup2 = isWelcomeLetterContractorForm(role);

    const methods = useForm({
        defaultValues: {
            tenantId: undefined,
            userId: undefined,
            applicantName: "",
            // Common internal fields for the form
            applicantSignature: "",
            applicantSignedAt: "",
            reviewedBySignedBy: "",
            reviewedBySignature: "",
            reviewedBySignedAt: "",

            // Group 1 Specific (matching API)
            hireDate: "",
            supervisor: "",
            firstPatientCareDate: "",

            // Group 2 Specific
            contractorAddress: "",
            agencyAddress: "",

            // Pay Rates (Flat in form, nested in payload)
            "payRate.initial_assessment_oasis": "",
            "payRate.recertification": "",
            "payRate.resumption": "",
            "payRate.discharge": "",
            "payRate.regular_visit": "",
            "payRate.iv_infusion": "",
            "payRate.blood_draw": "",
            "payRate.initial_assessment": "",

            applicantStatementAccepted: false,
            ...defaultValues
        },
        mode: "onChange"
    });

    const [status, setStatus] = useState("");
    const { user, companyName, seniorStaffNurse } = useCurrentUser();

    const {
        targetUserId,
        isSignatureModalOpen,
        openSignatureModal,
        closeSignatureModal,
        handleSignatureApply,
        handleStatusAction,
        signerName,
        isReadOnly,
        actualFromId,
        closeForm,
        handleSave,
    } = useApplicationFormLogic({
        methods,
        applicantNameField: "applicantName",
        applicantSignatureDateField: "applicantSignedAt",
        currentStatus: status,
        onSubmit: (data, flag, additionalData) => onSubmit(data, flag, additionalData),
        onClose
    });

    const { setValue, watch, reset, getValues } = methods;

    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [recordLoaded, setRecordLoaded] = useState(false);

    useEffect(() => {
        const fromUrl = parseRecordId(actualFromId);
        if (fromUrl) {
            setValue("id", fromUrl, { shouldDirty: false });
        }
    }, [actualFromId, setValue]);

    useEffect(() => {
        const fetchData = async () => {
            const effectiveUserId = targetUserId || user?.id;
            const recordIdFromUrl = parseRecordId(actualFromId);
            if (!effectiveUserId && !recordIdFromUrl) return;

            setLoading(true);
            try {
                const isAdminUser =
                    Number(user?.roleId) === 1 || Number(user?.role?.id) === 1;
                let data = null;

                if (recordIdFromUrl) {
                    try {
                        const byIdResponse = isAdminUser
                            ? await request(
                                  `${fetchEndpoint}/${recordIdFromUrl}?userId=${effectiveUserId}`,
                              )
                            : await request(`${fetchEndpoint}/${recordIdFromUrl}`);
                        if (byIdResponse.status === 1) {
                            data = pickRecordFromListResult(
                                byIdResponse.result,
                                effectiveUserId,
                            );
                        }
                    } catch (byIdError) {
                        console.error("Error fetching welcome letter by id:", byIdError);
                    }
                }

                if (!data && effectiveUserId) {
                    const listResponse = await request(
                        `${fetchEndpoint}?userId=${effectiveUserId}&role=${role}&limit=10`,
                    );
                    if (listResponse.status === 1) {
                        data = pickRecordFromListResult(
                            listResponse.result,
                            effectiveUserId,
                        );
                    }
                }

                if (data?.id) {
                    setStatus(data.status || "");
                    reset(mapWelcomeLetterToForm(data));
                    setRecordLoaded(true);
                }
            } catch (error) {
                console.error("Error fetching welcome letter:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [targetUserId, actualFromId, user?.id, role, fetchEndpoint, reset]);

    useEffect(() => {
        if (loading || recordLoaded || getValues("id")) return;

        if (targetUserId) setValue("userId", targetUserId);

        if (user) {
            if (!targetUserId && user.id) setValue("userId", user.id);
            if (user.tenantId) setValue("tenantId", user.tenantId);

            if (user.firstName && user.lastName) {
                const fullName = `${user.firstName} ${user.lastName}`;

                if (getValues('company') !== companyName) setValue('company', companyName);
                if (getValues('employeeName') !== fullName) setValue('employeeName', fullName);
                if (!getValues("contractorName")) setValue("contractorName", fullName);
                if (!getValues("effectiveDate")) {
                    if (user.hireDate) {
                        setValue("effectiveDate", displayDateUS(user.hireDate));
                    }
                }
            }

            if (user.hireDate && !getValues('hireDate')) {
                try {
                    const formattedHireDate = displayDateUS(user.hireDate);
                    if (formattedHireDate) {
                        setValue('hireDate', formattedHireDate);
                    }
                } catch (e) {
                    console.error("Error parsing user hireDate", e);
                }
            }

            if (seniorStaffNurse && !getValues('supervisorName')) {
                setValue('supervisorName', seniorStaffNurse);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setValue, targetUserId, user?.id, user?.tenantId, user?.roleId, user?.firstName, user?.lastName, user?.hireDate, companyName, getValues, isGroup1, isGroup2, loading, recordLoaded]);


    const onSubmit = async (data, flag, additionalData) => {
        try {
            const payload = buildWelcomeLetterPayload({
                data,
                flag,
                additionalData,
                role,
                fallbackTenantId: resolveFormTenantId(user?.tenantId),
                fallbackUserId: user?.id || 1,
                reviewerUserId: user?.id,
            });

            const recordId = parseRecordId(data.id, actualFromId);
            const method = recordId ? 'PUT' : 'POST';
            const url = recordId ? `${apiEndpoint}/${recordId}` : apiEndpoint;

            const response = await request(url, {
                method,
                body: JSON.stringify(payload)
            });

            if (response.status === 1) {
                const saved = response.result?.welcomeLetter;
                if (saved?.id) {
                    setValue("id", saved.id, { shouldDirty: false });
                }
                toast.success(response.message || "Welcome Letter processed successfully!", { id: "wl-submit" });
                closeForm();
            } else {
                toast.error(response.message || "Failed to process form!", { id: "wl-submit" });
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            const errMsg = error.message || "Unknown error occurred";
            toast.error(`Failed to submit form: ${errMsg}`, { id: "wl-submit" });
        }
    };

    const renderItem = (item) => {
        switch (item.type) {
            case 'intro':
                return (
                    <div key={item.id} className="mb-6 text-center">
                        <h2 className="text-xl font-bold mb-2 uppercase text-blue-900">{item.company}</h2>
                        <h3 className="text-lg font-bold mb-4 uppercase text-gray-800">{item.title}</h3>
                        <div className="flex flex-col md:flex-row justify-center items-center gap-2 mb-4">
                            <span className="text-gray-700">{item.salutation}</span>
                            {item.fields && item.fields.map(field => (
                                field.inputType === "date" ? (
                                    <InputDatePicker
                                        key={field.name}
                                        name={field.name}
                                        formatSeparator="/"
                                        compact
                                        className="min-w-[180px] max-w-[220px]"
                                        inputClassName="text-center"
                                    />
                                ) : (
                                    <input
                                        key={field.name}
                                        {...methods.register(field.name)}
                                        placeholder={field.placeholder}
                                        className="border-b-2 border-gray-300 focus:border-blue-500 outline-none px-2 py-1 text-center bg-transparent min-w-[200px]"
                                    />
                                )
                            ))}
                            <span className="text-gray-700">{item.salutationSuffix}</span>
                        </div>
                        {item.text && <p className="text-justify text-gray-700 whitespace-pre-line">{item.text}</p>}
                        {item.textAfter && <p className="text-justify text-gray-700 mt-2">{item.textAfter}</p>}
                    </div>
                );
            case 'field':
                return (
                    <div key={item.id} className="mb-4 text-left">
                        {item.inputType === "date" ? (
                            <InputDatePicker
                                formatSeparator="/"
                                name={item.name}
                                label={item.label}
                                disabled={item.disabled}
                            />
                        ) : (
                            <EvaluationInput
                                name={item.name}
                                label={item.label}
                                type={item.inputType || "text"}
                                placeholder={item.placeholder || ""}
                                required={item.required}
                                disabled={item.disabled}
                            />
                        )}
                    </div>
                );
            case 'text_header':
                return <h4 key={item.id} className="font-bold text-gray-800 mt-4 mb-2 text-left">{item.text}</h4>;
            case 'section_header':
                return <h3 key={item.id} className="text-lg font-bold text-gray-900 mt-8 mb-4 border-b pb-2 text-center uppercase">{item.title}</h3>;
            case 'pay_rate':
                return (
                    <div key={item.id} className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-center text-left">
                        <label className="font-medium text-gray-700">{item.label}</label>
                        <div className="flex items-center group">
                            <div className="relative flex items-center w-full max-w-[200px]">
                                <span className="absolute left-3 text-gray-400 font-semibold">$</span>
                                <input
                                    {...methods.register(item.name)}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>
                            <span className="bg-gray-50 border border-l-0 border-gray-300 rounded-r-lg px-4 py-2 text-gray-600 font-medium whitespace-nowrap">
                                {item.unit}
                            </span>
                        </div>
                    </div>
                );
            case 'text_block':
                return <p key={item.id} className="mb-4 text-justify text-gray-700 whitespace-pre-line">{item.text}</p>;
            case 'section':
                return (
                    <div key={item.id} className="mb-8 border-b border-gray-200 pb-6 text-left">
                        <h4 className="font-bold text-gray-900 mb-3">{item.title}</h4>
                        {item.text && <p className="text-justify text-gray-700 mb-3 whitespace-pre-line">{item.text}</p>}
                        {item.bullets && (
                            <ul className="list-disc pl-5 space-y-2 mb-3">
                                {item.bullets.map((b, idx) => (
                                    <li key={idx} className="text-gray-700 text-sm text-justify">{b}</li>
                                ))}
                            </ul>
                        )}
                        {item.definitions && item.definitions.map((def, idx) => (
                            <div key={idx} className="mb-3 pl-4 border-l-4 border-blue-100">
                                <span className="font-semibold text-gray-800">{def.term}:</span>
                                <span className="text-gray-700 text-sm ml-1 text-justify inline-block">{def.def}</span>
                            </div>
                        ))}
                        {item.content_after && item.content_after.map((content, idx) => {
                            if (content.mode === 'p') return <p key={idx} className="mb-2 text-justify text-gray-700 text-sm whitespace-pre-line">{content.text}</p>;
                            if (content.mode === 'subheading') return <h5 key={idx} className="font-semibold text-gray-800 mt-3 mb-1">{content.text}</h5>;
                            if (content.mode === 'list') return (
                                <ul key={idx} className="list-decimal pl-5 space-y-1 mb-2">
                                    {content.items.map((li, liIdx) => <li key={liIdx} className="text-gray-700 text-sm">{li}</li>)}
                                </ul>
                            );
                            if (content.mode === 'steps') return (
                                <ul key={idx} className="space-y-2 mb-2 mt-2">
                                    {content.items.map((step, stepIdx) => (
                                        <li key={stepIdx} className="text-sm text-gray-700">
                                            <span className="font-bold mr-2">{step.step}</span>
                                            {step.text}
                                        </li>
                                    ))}
                                </ul>
                            );
                            if (content.mode === 'address_fields') return (
                                <div key={idx} className="mt-4 ml-8 grid grid-cols-1 gap-4">
                                    {content.fields.map(field => (
                                        <div key={field.name} className="flex items-start gap-2">
                                            <label className="font-semibold">{field.label}</label>
                                            <textarea
                                                {...methods.register(field.name)}
                                                className="border border-gray-300 rounded p-1 text-sm w-full max-w-md"
                                                rows={3}
                                            />
                                        </div>
                                    ))}
                                </div>
                            );
                            return null;
                        })}
                    </div>
                );
            case 'part':
                return (
                    <div key={item.id} className="mb-8 text-left">
                        <h4 className="font-bold text-gray-900 mb-3 uppercase border-b border-gray-300 pb-1">
                            PART {item.partNumber}: {item.title}
                        </h4>
                        <div className="space-y-4">
                            {item.clauses.map((clause, idx) => (
                                <div key={idx} className="text-gray-700 text-sm text-justify">
                                    <div className="flex gap-2">
                                        <span className="font-semibold min-w-[30px]">{clause.number}</span>
                                        <div className="w-full">
                                            {clause.text}
                                            {clause.addressFields && (
                                                <div className="mt-4 ml-8 grid grid-cols-1 gap-4">
                                                    {clause.addressFields.map(field => (
                                                        <div key={field.name} className="flex items-start gap-2">
                                                            <label className="font-semibold">{field.label}</label>
                                                            <textarea
                                                                {...methods.register(field.name)}
                                                                className="border border-gray-300 rounded p-1 text-sm w-full max-w-md"
                                                                rows={3}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {clause.textAfter && <p className="mt-2">{clause.textAfter}</p>}
                                            {clause.subClauses && (
                                                <div className="mt-2 space-y-2 ml-4">
                                                    {clause.subClauses.map((sub, sIdx) => (
                                                        <div key={sIdx} className="flex gap-2">
                                                            <span className="font-semibold min-w-[30px]">{sub.number}</span>
                                                            <div className="w-full">
                                                                {sub.inputName ? (
                                                                    <div className="flex flex-wrap items-center gap-2 bg-gray-50 p-2 rounded-md border border-gray-100 mt-1">
                                                                        <div className="relative flex items-center">
                                                                            <span className="absolute left-2 text-gray-400 font-bold text-xs">$</span>
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                min="0"
                                                                                placeholder="0.00"
                                                                                {...methods.register(sub.inputName)}
                                                                                onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                                                                className="pl-5 pr-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none w-24 text-sm font-semibold"
                                                                            />
                                                                        </div>
                                                                        <span className="text-gray-600 text-xs font-medium">
                                                                            {sub.text.replace(/Dollars \(\$\.+\) /i, "").replace(/\(\$\.+\)/, "").trim()}
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-gray-700">{sub.text}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (loading) return <div className="text-center py-10 font-bold text-blue-600">Loading Form Data...</div>;

    return (
        <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
            <EvaluationFormHeader title={title || `Welcome Letter And Agreement (${role})`} onClose={closeForm} />
            <div className="p-8 md:p-12">
                <CorrectionRequestAlert status={status} comments={watch("comments")} />
                <FormProvider {...methods}>
                    <form className="space-y-6">
                        <fieldset disabled={isReadOnly} className="disabled:opacity-80 disabled:pointer-events-none">
                            {page === 1 && (
                                <div className="animate-fade-in">
                                    {items.slice(0, splitIndex).map(item => renderItem(item))}
                                </div>
                            )}
                            {page === 2 && (
                                <div className="animate-fade-in">
                                    {items.slice(splitIndex).map(item => renderItem(item))}
                                </div>
                            )}
                        </fieldset>
                    </form>
                    <ApplicationFormFooter
                        page={page}
                        setPage={setPage}
                        totalPages={totalPages}
                        methods={methods}
                        onSave={handleSave}
                        onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                        onStatusUpdate={handleStatusAction}
                        userRole={user?.roleId || 1}
                        isSignatureModalOpen={isSignatureModalOpen}
                        openSignatureModal={openSignatureModal}
                        closeSignatureModal={closeSignatureModal}
                        handleSignatureApply={handleSignatureApply}
                        signerName={signerName}
                        isReadOnly={isReadOnly}
                        applicantName={watch('applicantName') || watch('contractorName') || watch('employeeName')}
                        applicantSignature={watch('applicantSignature')}
                        applicantSignedAt={watch('applicantSignedAt')}
                        reviewedBySignedBy={watch('reviewedBySignedBy')}
                        reviewedBySignature={watch('reviewedBySignature')}
                        reviewedBySignedAt={watch('reviewedBySignedAt')}
                        canSignEmployee={!isReadOnly && (user?.roleId === 1 || user?.roleId === undefined)}
                        canSignAdmin={!isReadOnly && user?.roleId !== 1}
                        currentStatus={status}
                    />
                </FormProvider>
            </div>

        </div >
    );
};

export default BaseWelcomeLetter;
